import { use, useMemo } from "react";
import { GQL, Utils } from "@binale-tech/shared";
import type { GenericRecord } from "@domain/models";
import { RecordsContext } from "@domain/context/accountingData/RecordsCtx";
import { PaymentsContext, type PaymentsCtxData } from "@domain/context/accountingData/PaymentsProvider";
import { YearPeriodContext } from "@domain/context/CompanyContext";
import { ReadinessContext } from "@domain/context/DataReadyness";
import { type MetricStatus } from "./useGuVMetrics";

export interface OpenPositionsSide {
    /** net open brutto, cents (Gutschriften net against invoices) */
    total: number;
    /** records with open amount ≠ 0 */
    count: number;
}

export interface OpenPositions {
    status: MetricStatus;
    /** open Deb - customers owe us */
    receivables: OpenPositionsSide;
    /** open ER + ER_A - we owe suppliers */
    payables: OpenPositionsSide;
}

export const computeOpenPositions = (
    yearRecords: GenericRecord[],
    recordRelation: PaymentsCtxData["recordRelation"],
): { receivables: OpenPositionsSide; payables: OpenPositionsSide; sourceCount: number } => {
    const receivables: OpenPositionsSide = { total: 0, count: 0 };
    const payables: OpenPositionsSide = { total: 0, count: 0 };
    let sourceCount = 0;
    yearRecords.forEach((record) => {
        // same exclusions as the Saldenvortrag source selection (useSaldoTransferInput)
        if (record.draft || record.cancellation) {
            return;
        }
        const pk = record.getProductKey();
        if (!Utils.PaymentUtils.isProductPaymentSource(pk)) {
            return;
        }
        sourceCount += 1;
        const open = record.getOpenBrutto(recordRelation.get(record.key) ?? []);
        if (open === 0) {
            return;
        }
        const side = pk === GQL.IProductKey.Deb ? receivables : payables;
        side.total += open;
        side.count += 1;
    });
    return { receivables, payables, sourceCount };
};

const ZERO_SIDE: OpenPositionsSide = { total: 0, count: 0 };

export const useOpenPositions = (): OpenPositions => {
    const { isReady } = use(ReadinessContext);
    const { allRecords } = use(RecordsContext);
    const { year } = use(YearPeriodContext);
    const payments = use(PaymentsContext);

    const yearRecords = useMemo(
        () => (allRecords.list || []).filter((record) => record.date.getFullYear() === year),
        [allRecords, year],
    );

    return useMemo(() => {
        if (!isReady || !payments.isLoaded) {
            return { status: "loading" as const, receivables: ZERO_SIDE, payables: ZERO_SIDE };
        }
        const { receivables, payables, sourceCount } = computeOpenPositions(yearRecords, payments.recordRelation);
        if (sourceCount === 0) {
            return { status: "empty" as const, receivables, payables };
        }
        return { status: "ready" as const, receivables, payables };
    }, [isReady, payments, yearRecords]);
};
